import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { HotelSearchWidget } from '../HotelSearchWidget';
import { SearchQuery, ApiLogEntry, SearchType } from '../../types';
import { useLocalization } from '../../hooks/useLocalization';
import { usePageContent } from '../../hooks/usePageContent';
import { subscribeToNewsletter } from '../../services/mailchimpService';
import { getArrivalAutoComplete } from '../../services/tourvisioService';

interface HotelsPageProps {
    logApiCall: (log: ApiLogEntry) => void;
    isSettingsReady: boolean;
}

interface FeaturedHotel {
    id: string;
    tourvisioId: string;
    name: string;
    stars: number;
    location: { city: string; country: string };
    thumbnail: string;
    shortDescription?: string;
    displayPrice?: { amount: number; currency: string };
}

export const HotelsPage: React.FC<HotelsPageProps> = ({ logApiCall, isSettingsReady }) => {
    const { t, language } = useLocalization();
    const { content } = usePageContent('hotels', language);
    const navigate = useNavigate();

    const [featuredHotels, setFeaturedHotels] = useState<FeaturedHotel[]>([]);
    const [loadingHotels, setLoadingHotels] = useState(true);
    const [heroSlide, setHeroSlide] = useState(0);

    // Newsletter
    const [email, setEmail] = useState('');
    const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [subMessage, setSubMessage] = useState('');

    const heroTitle = content?.hero?.title || (language === 'sv' ? 'Hitta Ditt Perfekta Boende' : 'Find Your Perfect Stay');
    const heroSubtitle = content?.hero?.subtitle || (language === 'sv' ? 'Från mysiga boutiquehotell till 5-stjärniga lyxresorts. Boka med trygghet.' : 'From cozy boutique hotels to 5-star luxury resorts. Book with confidence.');
    const heroSlides = [
        {
            image: content?.hero?.imageUrl || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2000&auto=format&fit=crop',
            label: 'Antalya'
        },
        {
            image: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?q=80&w=2000&auto=format&fit=crop',
            label: 'Bodrum'
        },
        {
            image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?q=80&w=2000&auto=format&fit=crop',
            label: 'Santorini'
        },
        {
            image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?q=80&w=2000&auto=format&fit=crop',
            label: 'Maldives'
        },
        {
            image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2000&auto=format&fit=crop',
            label: 'Dubai'
        }
    ];

    // Auto-rotate hero slider
    useEffect(() => {
        const interval = setInterval(() => {
            setHeroSlide(prev => (prev + 1) % heroSlides.length);
        }, 6000);
        return () => clearInterval(interval);
    }, [heroSlides.length]);

    // Fetch featured hotels
    useEffect(() => {
        const fetchFeatured = async () => {
            try {
                const res = await fetch('/api/hotels?featured=true&active=true&limit=6');
                const data = await res.json();
                if (data.success && data.hotels) {
                    setFeaturedHotels(data.hotels);
                }
            } catch (err) {
                console.error('Failed to fetch featured hotels:', err);
            } finally {
                setLoadingHotels(false);
            }
        };
        fetchFeatured();
    }, []);

    const handleSearch = (query: SearchQuery) => {
        navigate('/results', { state: { searchQuery: query, isLoading: true } });
    };

    // Auto-search from URL params (when coming from restaurant sidebar)
    const [searchParams] = useSearchParams();
    const [autoSearchDone, setAutoSearchDone] = useState(false);
    useEffect(() => {
        if (autoSearchDone || !isSettingsReady) return;
        const destParam = searchParams.get('destination');
        const checkInParam = searchParams.get('checkIn');
        const nightsParam = searchParams.get('nights');
        if (!destParam) return;

        setAutoSearchDone(true);
        // Resolve destination via autocomplete then navigate to results
        (async () => {
            try {
                const culture = language === 'sv' ? 'sv-SE' : 'en-US';
                const results = await getArrivalAutoComplete(destParam, 2, culture, logApiCall);
                if (results && results.length > 0) {
                    const first = results[0];
                    const destId = String(first.city?.id ?? first.state?.id ?? first.country?.id ?? first.id ?? '');
                    const destName = first.city?.name || first.state?.name || first.country?.name || first.name || destParam;
                    const destType = first.city ? 2 : first.country ? 1 : 2;
                    handleSearch({
                        type: SearchType.OnlyHotel,
                        departureId: '',
                        destinationId: destId,
                        destinationName: destName,
                        destinationType: destType,
                        checkIn: checkInParam || new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
                        nights: parseInt(nightsParam || '4'),
                        rooms: 1,
                        nationality: 'SE',
                        currency: 'SEK',
                        adults: parseInt(searchParams.get('adults') || '2'),
                        children: 0,
                        childrenAges: []
                    });
                }
            } catch (err) {
                console.error('Auto-search failed:', err);
            }
        })();
    }, [isSettingsReady, searchParams, autoSearchDone, language, logApiCall]);

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;
        setSubStatus('loading');
        const result = await subscribeToNewsletter(email);
        if (result.success) {
            setSubStatus('success');
            setSubMessage(result.message);
            setEmail('');
        } else {
            setSubStatus('error');
            setSubMessage(result.message);
        }
    };

    const StarRating: React.FC<{ stars: number }> = ({ stars }) => (
        <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
                <svg key={i} className={`w-4 h-4 ${i < stars ? 'text-amber-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            {/* Hero Section with Destination Slider */}
            <div className="relative h-[80vh] min-h-[550px] w-full overflow-hidden">
                <div className="absolute inset-0">
                    {heroSlides.map((slide, idx) => (
                        <img
                            key={idx}
                            src={slide.image}
                            className={`absolute w-full h-full object-cover transform scale-105 transition-opacity duration-1000 ${idx === heroSlide ? 'opacity-100' : 'opacity-0'}`}
                            alt={slide.label}
                        />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-gray-50/90"></div>
                </div>

                <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center items-center text-center pt-12 pb-32">
                    <span className="inline-block py-1 px-4 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-bold uppercase tracking-widest mb-6 animate-fade-in-up">
                        {content?.hero?.badgeText || (language === 'sv' ? '🏨 Bara Hotell' : '🏨 Hotel Only')}
                    </span>
                    <h1 className="text-4xl md:text-7xl font-black text-white mb-4 leading-tight tracking-tight animate-fade-in-up">
                        {heroTitle}
                    </h1>
                    <p className="text-lg md:text-xl text-white/90 max-w-3xl font-light animate-fade-in-up">
                        {heroSubtitle}
                    </p>
                </div>

                {/* Destination label + Slide indicators */}
                <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 flex items-center gap-4 z-10">
                    <span className="text-white/80 text-sm font-medium tracking-wide">
                        📍 {heroSlides[heroSlide]?.label}
                    </span>
                    <div className="flex gap-2">
                        {heroSlides.map((_, idx) => (
                            <button key={idx} onClick={() => setHeroSlide(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${idx === heroSlide ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'}`} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Search Widget */}
            <div className="relative z-20 -mt-[92px] mb-20 animate-fade-in-up">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="transform transition-all duration-300 hover:-translate-y-1">
                        <HotelSearchWidget
                            onSearch={handleSearch}
                            logApiCall={logApiCall}
                            isSettingsReady={isSettingsReady}
                        />
                    </div>
                </div>
            </div>

            {/* Why Book With Us */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                <div className="text-center mb-12">
                    <span className="text-emerald-600 font-bold uppercase tracking-wider text-xs">
                        {language === 'sv' ? 'Varför Lumitours' : 'Why Lumitours'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">
                        {content?.benefits_title?.title || (language === 'sv' ? 'Boka Hotell Med Trygghet' : 'Book Hotels With Confidence')}
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 text-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg mb-2">{content?.feature_1?.title || (language === 'sv' ? 'Stadsresor' : 'City Breaks')}</h3>
                        <p className="text-gray-500">{content?.feature_1?.description || (language === 'sv' ? 'Centralt belägna hotell perfekta för att utforska.' : 'Centrally located hotels perfect for exploring.')}</p>
                    </div>
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 text-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg mb-2">{content?.feature_2?.title || (language === 'sv' ? 'Lyx & Spa' : 'Luxury & Spa')}</h3>
                        <p className="text-gray-500">{content?.feature_2?.description || (language === 'sv' ? 'Njut av avkoppling med vårt premiumutbud.' : 'Indulge in relaxation with our premium wellness collection.')}</p>
                    </div>
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-teal-200 text-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg mb-2">{content?.feature_3?.title || (language === 'sv' ? 'Bästa Priser' : 'Best Prices')}</h3>
                        <p className="text-gray-500">{content?.feature_3?.description || (language === 'sv' ? 'Vi garanterar bästa värde för ditt val.' : 'We guarantee the best value for your selected destination.')}</p>
                    </div>
                </div>
            </div>

            {/* Featured Hotels */}
            {featuredHotels.length > 0 && (
                <div className="py-24 bg-white border-t border-gray-100">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <span className="text-amber-600 font-bold uppercase tracking-wider text-xs">
                                {language === 'sv' ? 'Handplockade' : 'Handpicked'}
                            </span>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2">
                                {content?.featured_title?.title || (language === 'sv' ? 'Utvalda Hotell' : 'Featured Hotels')}
                            </h2>
                            <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">
                                {content?.featured_title?.subtitle || (language === 'sv' ? 'Våra mest populära boenden, noggrant utvalda för dig.' : 'Our most popular stays, carefully curated for you.')}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {featuredHotels.map((hotel) => (
                                <Link
                                    key={hotel.id}
                                    to={`/hotels/${hotel.tourvisioId}`}
                                    className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group cursor-pointer block"
                                >
                                    <div className="h-56 overflow-hidden relative">
                                        {hotel.thumbnail ? (
                                            <img src={hotel.thumbnail} alt={hotel.name}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-5xl">🏨</div>
                                        )}
                                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors"></div>
                                        {hotel.displayPrice?.amount && (
                                            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md">
                                                <span className="text-sm font-bold text-gray-900">
                                                    {language === 'sv' ? 'från' : 'from'} {hotel.displayPrice.amount.toLocaleString()} {hotel.displayPrice.currency || 'SEK'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center justify-between mb-2">
                                            <StarRating stars={hotel.stars || 0} />
                                            <span className="text-xs font-medium text-gray-400 flex items-center gap-1">
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                {hotel.location?.city}, {hotel.location?.country}
                                            </span>
                                        </div>
                                        <h3 className="font-bold text-xl text-gray-900 leading-tight mb-2 group-hover:text-emerald-700 transition-colors flex items-center justify-between">
                                            {hotel.name}
                                            <svg className="h-5 w-5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </h3>
                                        {hotel.shortDescription && (
                                            <p className="text-sm text-gray-500 line-clamp-2">{hotel.shortDescription}</p>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Loading skeleton for featured hotels */}
            {loadingHotels && (
                <div className="py-24 bg-white border-t border-gray-100">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <div className="h-4 w-24 bg-gray-200 rounded mx-auto mb-3 animate-pulse"></div>
                            <div className="h-8 w-64 bg-gray-200 rounded mx-auto animate-pulse"></div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="bg-white border border-gray-100 rounded-2xl overflow-hidden animate-pulse">
                                    <div className="h-56 bg-gray-200"></div>
                                    <div className="p-5 space-y-3">
                                        <div className="h-4 w-24 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-48 bg-gray-200 rounded"></div>
                                        <div className="h-4 w-full bg-gray-200 rounded"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Hotel Booking Benefits Banner */}
            <div className="py-16 bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-900">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                {content?.promo?.title || (language === 'sv' ? 'Flexibla Bokningsvillkor' : 'Flexible Booking Terms')}
                            </h2>
                            <p className="text-slate-300 max-w-xl">
                                {content?.promo?.subtitle || (language === 'sv'
                                    ? 'Gratis avbokning upp till 48 timmar före incheckning på utvalda hotell. Boka nu, betala senare.'
                                    : 'Free cancellation up to 48 hours before check-in on selected hotels. Book now, pay later.')}
                            </p>
                        </div>
                        <div className="flex gap-6 text-center">
                            <div className="flex flex-col items-center">
                                <div className="w-14 h-14 bg-emerald-500/20 border border-emerald-400/30 rounded-xl flex items-center justify-center mb-2">
                                    <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                                </div>
                                <span className="text-white text-sm font-bold">{language === 'sv' ? 'Säker Bokning' : 'Secure'}</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-14 h-14 bg-amber-500/20 border border-amber-400/30 rounded-xl flex items-center justify-center mb-2">
                                    <svg className="w-7 h-7 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                                <span className="text-white text-sm font-bold">{language === 'sv' ? 'Bästa Pris' : 'Best Price'}</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-14 h-14 bg-blue-500/20 border border-blue-400/30 rounded-xl flex items-center justify-center mb-2">
                                    <svg className="w-7 h-7 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                </div>
                                <span className="text-white text-sm font-bold">24/7 {language === 'sv' ? 'Support' : 'Support'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Newsletter Section */}
            <div className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        {language === 'sv' ? 'Håll Dig Uppdaterad' : 'Stay Updated'}
                    </h2>
                    <p className="text-blue-100 mb-8 max-w-xl mx-auto">
                        {language === 'sv'
                            ? 'Få exklusiva hotellerbjudanden och tips direkt till din inkorg.'
                            : 'Get exclusive hotel deals and tips delivered to your inbox.'}
                    </p>
                    <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={language === 'sv' ? 'Din e-postadress' : 'Your email address'}
                            className="flex-1 px-5 py-3 rounded-full text-gray-900 bg-white focus:ring-2 focus:ring-amber-400 outline-none"
                            required
                        />
                        <button type="submit" disabled={subStatus === 'loading'}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-3 rounded-full transition-all shadow-lg disabled:opacity-50">
                            {subStatus === 'loading' ? '...' : (language === 'sv' ? 'Prenumerera' : 'Subscribe')}
                        </button>
                    </form>
                    {subStatus === 'success' && <p className="text-green-300 mt-4">{subMessage}</p>}
                    {subStatus === 'error' && <p className="text-red-300 mt-4">{subMessage}</p>}
                </div>
            </div>
        </div>
    );
}